import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import VerticalCard from '@/components/marketing/VerticalCard';
import FAQAccordion from '@/components/marketing/FAQAccordion';
import { Button } from '@/components/ui/button';
import { VERTICALS, FORMATS } from '@/data/site-content';
import { breadcrumbJsonLd, serviceJsonLd, graph } from '@/lib/jsonld';

export default function VerticalDetail() {
  const { slug } = useParams();
  const v = VERTICALS.find((x) => x.slug === slug);
  if (!v) return <Navigate to="/verticals" replace />;
  const Icon = v.icon;
  const others = VERTICALS.filter((x) => x.slug !== v.slug).slice(0, 3);
  const relatedFormats = (v.relatedFormats || [])
    .map((s) => FORMATS.find((x) => x.slug === s))
    .filter(Boolean);

  const faqJsonLd = Array.isArray(v.faq) && v.faq.length > 0 ? {
    '@type': 'FAQPage',
    mainEntity: v.faq.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  } : null;

  const jsonLdNodes = [
    breadcrumbJsonLd([
      { label: 'Home', path: '/' },
      { label: 'Verticals', path: '/verticals' },
      { label: v.title },
    ]),
    serviceJsonLd({
      name: `${v.title} traffic`,
      description: v.intro,
      serviceType: 'Performance traffic for vertical',
      url: `/verticals/${v.slug}`,
    }),
  ];
  if (faqJsonLd) jsonLdNodes.push(faqJsonLd);

  return (
    <>
      <SEOHead path={`/verticals/${v.slug}`} jsonLd={graph(...jsonLdNodes)} />
      <PageHeader
        breadcrumbs={[{ label: 'Verticals', to: '/verticals' }, { label: v.title }]}
        eyebrow="Vertical"
        title={v.title}
        subtitle={v.intro}
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <Button asChild className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl transition-colors">
            <Link to="/apply" data-testid={`vertical-${v.slug}-apply-cta`} className="inline-flex items-center gap-2">Apply as Advertiser <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline" className="bg-white/5 text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl transition-colors">
            <Link to="/verticals">All verticals</Link>
          </Button>
        </div>
      </PageHeader>
      <section className="bg-[#F5F7FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 prose-dashbound">
            <h2>Why this vertical works with performance traffic</h2>
            <p>{v.intro}</p>
            <h2>Recommended traffic formats</h2>
            <p>{v.formats.join(', ')}.</p>
            <h2>Targeting and optimization tips</h2>
            <ul>
              {v.optimization.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
            <h2>Example campaign goals</h2>
            <ul>
              {v.goals.map((g, i) => <li key={i}>{g}</li>)}
            </ul>
            <h2>How Ads Dashbound supports advertisers</h2>
            <p>Your application is reviewed by a real human. We share recommended formats, GEOs and source rules before you launch — and stay close during the first 72 hours of spend.</p>
            {v.disclaimer && (
              <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-5 not-prose">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-900"><strong>Compliance note:</strong> {v.disclaimer}</p>
                </div>
              </div>
            )}
          </div>
          <aside className="space-y-4">
            <div className="rounded-2xl bg-white border border-slate-900/10 p-6 shadow-[0_10px_30px_rgba(2,6,23,0.06)]">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#7C3AED]/10 text-[#7C3AED] border border-[#7C3AED]/20">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="font-display mt-4 text-base font-semibold text-slate-900">Quick facts</h3>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Top formats</dt><dd className="text-slate-900 font-medium text-right">{v.formats.join(', ')}</dd></div>
              </dl>
            </div>
            <div className="rounded-2xl bg-[#07111F] text-white border border-white/10 p-6">
              <h3 className="font-display text-base font-semibold">What you’ll get</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                <li className="inline-flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#22D3EE]" /> Format & GEO guidance</li>
                <li className="inline-flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#22D3EE]" /> Launch checklist</li>
                <li className="inline-flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#22D3EE]" /> Source-level controls</li>
                <li className="inline-flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#22D3EE]" /> Human account manager</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
      <section className="bg-[#07111F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-2xl font-bold">Other verticals to consider</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {others.map((o) => <VerticalCard key={o.slug} vertical={o} />)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {Array.isArray(v.faq) && v.faq.length > 0 && (
        <section className="bg-[#F5F7FB]" data-testid={`vertical-${v.slug}-faq-section`}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#2563EB]">FAQ</span>
            <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold text-slate-900">Frequently asked questions</h2>
            <div className="mt-6 rounded-2xl bg-white border border-slate-900/10 px-6 shadow-[0_10px_30px_rgba(2,6,23,0.04)]">
              <FAQAccordion items={v.faq} idPrefix={`vert-${v.slug}`} />
            </div>
          </div>
        </section>
      )}

      {/* Related formats internal linking */}
      {relatedFormats.length > 0 && (
        <section className="bg-[#07111F] text-white" data-testid={`vertical-${v.slug}-related-formats-section`}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="font-display text-xl sm:text-2xl font-bold">Recommended formats for {v.title}</h2>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {relatedFormats.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/ad-formats/${r.slug}`}
                    data-testid={`vertical-${v.slug}-related-format-${r.slug}`}
                    className="group flex items-start gap-3 rounded-2xl bg-white/[0.04] border border-white/10 p-4 backdrop-blur-md hover:border-[#22D3EE]/40 transition-colors"
                  >
                    <ArrowRight className="h-4 w-4 mt-0.5 text-[#22D3EE] transition-transform group-hover:translate-x-0.5" />
                    <span>
                      <span className="block text-sm font-semibold text-white">{r.title}</span>
                      <span className="block text-xs text-slate-400 mt-0.5">{r.short}</span>
                    </span>
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/apply"
                  data-testid={`vertical-${v.slug}-related-apply`}
                  className="group flex items-start gap-3 rounded-2xl bg-white/[0.04] border border-white/10 p-4 backdrop-blur-md hover:border-[#22D3EE]/40 transition-colors"
                >
                  <ArrowRight className="h-4 w-4 mt-0.5 text-[#22D3EE] transition-transform group-hover:translate-x-0.5" />
                  <span>
                    <span className="block text-sm font-semibold text-white">Apply as Advertiser</span>
                    <span className="block text-xs text-slate-400 mt-0.5">Submit your offers, GEOs and budget for review.</span>
                  </span>
                </Link>
              </li>
            </ul>
          </div>
        </section>
      )}

      <CTASection primaryTo="/apply" />
    </>
  );
}
